import { MNSproCloudApi } from '..';
import {
  OrganizationContactModel,
  OrganizationContactUpdateRequest,
  OrganizationCreateRequest,
  OrganizationResponse,
  OrganizationUpdateRequest,
  PeriodCreate,
  PeriodUpdate,
  SecurityManagerModel,
  StructuralChangeStatusResponse,
} from '../api/v2';

/**
 * Service class which defines methods to be used for MNSpro Cloud Organization management.
 *
 * @class Organization
 */
class OrganizationService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.OrganizationsApiFactory(config);

    return api;
  }

  static async create(
    authToken: string,
    tenantId: string,
    name: string,
    domains: string[],
    period: PeriodCreate,
  ) {
    const api = OrganizationService.getApi(authToken);
    const createRequest: OrganizationCreateRequest = {
      tenantId,
      name,
      domains,
      period,
    };
    const response = await api.create(createRequest);

    return response.data;
  }

  static async get(
    authToken: string,
    organizationId: string,
  ): Promise<OrganizationResponse> {
    const api = OrganizationService.getApi(authToken);
    const resp = await api.get(organizationId);
    return resp.data;
  }

  static async startAppleSync(authToken: string, organizationId: string) {
    const api = OrganizationService.getApi(authToken);

    const settingsResponse = await api.startAppleSync(organizationId);
    return settingsResponse.data;
  }

  static async getDomains(authToken: string, organizationId: string) {
    const api = OrganizationService.getApi(authToken);
    const resp = await api.getDomains(organizationId);

    return resp.data;
  }

  static async getAll(
    tenantId: string,
    authToken: string,
  ): Promise<OrganizationResponse[]> {
    const api = OrganizationService.getApi(authToken);
    const result = await api.getAll(tenantId);
    return result.data;
  }

  static async deleteMetaInformation(
    authToken: string,
    organizationId: string,
  ) {
    const api = OrganizationService.getApi(authToken);
    await api.deleteMetaInformation(organizationId);
  }

  static async deleteAllPeriods(authToken: string, organizationId: string) {
    const api = OrganizationService.getApi(authToken);
    const response = await api.getPeriodsAll(organizationId);

    const promises = [];

    for (const period of response.data) {
      if (period.id) {
        promises.push(api.deletePeriod(organizationId, period.id));
      }
    }

    await Promise.all(promises);
  }

  static async getOrganization(
    authToken: string,
    organizationId: string,
  ): Promise<OrganizationResponse> {
    const api = OrganizationService.getApi(authToken);
    const response = await api.get(organizationId);

    return response.data;
  }

  static async update(
    authToken: string,
    organizationId: string,
    name: string,
    domains: string[],
  ) {
    const api = OrganizationService.getApi(authToken);
    const updateRequest: OrganizationUpdateRequest = {
      name,
      organizationId,
      domains,
    };

    const response = await api.update(updateRequest);

    return response.data;
  }

  static async updateContact(
    authToken: string,
    organizationId: string,
    organizationContact: OrganizationContactModel,
    securityManager: SecurityManagerModel,
  ) {
    const api = OrganizationService.getApi(authToken);

    const contactUpdateRequest: OrganizationContactUpdateRequest = {
      organizationId,
      organizationContact,
      securityManager,
    };

    const response = await api.updateContact(
      organizationId,
      contactUpdateRequest,
    );

    return response.data;
  }

  static async deleteOrganization(authToken: string, organizationId: string) {
    const api = OrganizationService.getApi(authToken);
    await api._delete(organizationId);
  }

  static async addPeriod(
    authToken: string,
    organizationId: string,
    name: string,
    start: string,
    end: string,
    active: boolean,
  ) {
    const api = OrganizationService.getApi(authToken);
    const periodCreate: PeriodCreate = {
      end,
      name,
      start,
      active,
    };
    const response = await api.createPeriod(organizationId, periodCreate);

    return response.data;
  }

  static async updatePeriod(
    authToken: string,
    organizationId: string,
    periodId: string,
    start: string,
    end: string,
  ) {
    const api = OrganizationService.getApi(authToken);
    const periodUpdate: PeriodUpdate = {
      start,
      end,
    };
    const response = await api.updatePeriod(
      organizationId,
      periodId,
      periodUpdate,
    );

    return response.data;
  }

  static async deletePeriod(
    authToken: string,
    organizationId: string,
    periodId: string,
  ) {
    const api = OrganizationService.getApi(authToken);
    await api.deletePeriod(organizationId, periodId);
  }

  static async activatePeriod(
    authToken: string,
    organizationId: string,
    periodId: string,
  ) {
    const api = OrganizationService.getApi(authToken);
    await api.activatePeriod(organizationId, periodId);
  }

  /**
   * Returns the structural change (user import, time period switch, timetable import, ...) that is
   * currently running for the organization. Structural changes are serialized per organization, so a
   * running one means a newly started change has to wait for it.
   *
   * @param {string} authToken JWT (without 'Bearer'-prefix).
   * @param {string} organizationId Id of the organization.
   * @return {Promise<StructuralChangeStatusResponse>} The running structural change, if any.
   */
  static async getStructuralChange(
    authToken: string,
    organizationId: string,
  ): Promise<StructuralChangeStatusResponse> {
    const api = OrganizationService.getApi(authToken);
    const response = await api.getStructuralChange(organizationId);

    return response.data;
  }
}

export { OrganizationService };
